/**
 * Die erste Frage: Was soll passieren?
 *
 * Zwei Wege, die sich nie mischen. Im Backend heißen sie `neuanlage` und `felder`; hier
 * heißen sie so, wie ein Mensch sie sagen würde. Die Wahl steht am Anfang, weil sie
 * alles danach bestimmt — welche Artikel zählen (die neuen oder die vorhandenen), welche
 * Schritte folgen, und was am Ende in Plenty passiert.
 */
import { PackagePlus, PencilLine } from 'lucide-react';

/** Die zwei Pfade der Oberfläche. Backend-Werte, siehe `models/ArtikelimportEntwurf`. */
export type Betriebsart = 'neuanlage' | 'felder';

export const BETRIEBSARTEN: Array<{
  schluessel: Betriebsart;
  titel: string;
  kurz: string;
  erklaerung: string;
  icon: typeof PackagePlus;
}> = [
  {
    schluessel: 'neuanlage',
    titel: 'Neue Artikel anlegen',
    kurz: 'Neu anlegen',
    erklaerung: 'Für Artikel, die es in Plenty noch nicht gibt. Sie entstehen inaktiv, '
      + 'mit Varianten, Preisen, Kategorien, Bildern, Eigenschaften und Tags. Was schon '
      + 'existiert, wird automatisch übersprungen.',
    icon: PackagePlus,
  },
  {
    schluessel: 'felder',
    titel: 'Bestand erweitern / aktualisieren',
    kurz: 'Aktualisieren',
    erklaerung: 'Für Artikel, die schon in Plenty stehen. Zugeordnet über die EAN. Fehlende '
      + 'Felder werden gefüllt (Zolltarifnummer, Gewicht, Maße …), fehlende Bilder '
      + 'nachgezogen. Überschrieben wird nur, was du ausdrücklich anhakst.',
    icon: PencilLine,
  },
];

export function betriebsartName(schluessel: string | null | undefined, lang = false): string {
  const b = BETRIEBSARTEN.find(x => x.schluessel === schluessel);
  if (!b) return schluessel ?? '–';
  return lang ? b.titel : b.kurz;
}

export default function BetriebsartWahl({
  wert, aufWahl, kompakt = false,
}: {
  wert: Betriebsart | null;
  aufWahl: (b: Betriebsart) => void;
  /** Kleine Bauform für den Kopf eines bestehenden Entwurfs. */
  kompakt?: boolean;
}) {
  if (kompakt) {
    return (
      <div className="flex flex-wrap gap-2">
        {BETRIEBSARTEN.map(b => (
          <button
            key={b.schluessel}
            type="button"
            onClick={() => aufWahl(b.schluessel)}
            className={`rounded border px-3 py-1.5 text-sm ${
              wert === b.schluessel
                ? 'border-[#1A1A1A] bg-[#E9FF57] font-medium text-[#1A1A1A]'
                : 'border-[#DAD9D9] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]'
            }`}
          >
            {b.kurz}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {BETRIEBSARTEN.map(b => {
        const Icon = b.icon;
        const aktiv = wert === b.schluessel;
        return (
          <button
            key={b.schluessel}
            type="button"
            onClick={() => aufWahl(b.schluessel)}
            className={`flex items-start gap-3 rounded border-2 p-4 text-left transition-colors ${
              aktiv
                ? 'border-[#1A1A1A] bg-[#E9FF57]/40'
                : 'border-[#DAD9D9] bg-white hover:border-[#1A1A1A]'
            }`}
            aria-pressed={aktiv}
          >
            <Icon className="mt-0.5 h-6 w-6 shrink-0 text-[#1A1A1A]" aria-hidden />
            <span className="min-w-0">
              <span className="block font-semibold text-[#1A1A1A]">{b.titel}</span>
              <span className="mt-1 block text-sm text-[#1A1A1A]/70">{b.erklaerung}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
